<script setup lang="ts">
import { getAssetsUrlForCDN } from '@/utils/template';

type Props = {
  // Mensagem já resolvida por kind na store (rede, 429, delivery_failed, api).
  message?: string | null;
};

const { message = null } = defineProps<Props>();

const emit = defineEmits<{ retry: []; close: [] }>();

const isOpen = defineModel<boolean>('modelValue', { default: false });

// Mesmo X das outras telas da roletinha.
const closeIcon = getAssetsUrlForCDN('/assets/images/roleta/close.svg');

// Fechar/X/backdrop: dispensa o erro E pede refresh da roleta (o pai re-resolve),
// pra não voltar pra roda com o estado velho e girar a roleta antiga em loop.
function close() {
  isOpen.value = false;
  emit('close');
}

// "Tentar novamente": fecha o modal e devolve o controle pro componente girar de
// novo (as falhas de giro são retryáveis — o giro não foi consumido/foi estornado).
// Fecha direto (sem emitir 'close') pra não disparar o refresh junto do novo giro.
function retry() {
  isOpen.value = false;
  emit('retry');
}
</script>

<template>
  <!-- Camada interna do sheet da roleta (mesma estratégia do popup de resultado):
       backdrop próprio + card centrado, respeitando os cantos do .rlt-screen. -->
  <Transition name="rlt-popup">
    <div
      v-if="isOpen"
      class="roulette-error-layer"
      @click.self="close"
      @dragstart.prevent
    >
      <div class="roulette-error">
        <button
          aria-label="Fechar"
          class="roulette-error__close"
          type="button"
          @click="close"
        >
          <img
            alt=""
            :src="closeIcon"
          />
        </button>

        <div
          aria-hidden="true"
          class="roulette-error__icon"
        >
          !
        </div>

        <h2 class="roulette-error__title">Ops!</h2>

        <p class="roulette-error__message">
          {{ message || 'Erro ao girar a roleta.' }}
        </p>

        <div class="roulette-error__actions">
          <button
            class="roulette-error__btn"
            type="button"
            @click="retry"
          >
            Tentar novamente
          </button>
          <button
            class="roulette-error__btn roulette-error__btn--ghost"
            type="button"
            @click="close"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
// Backdrop interno: cobre só o sheet da roleta. O z vem da escala de camadas
// declarada no .rlt-screen (RouletteGame) — mesmo nível do popup de resultado.
.roulette-error-layer {
  @apply absolute inset-0 flex items-center justify-center select-none;

  z-index: var(--rlt-z-popup, 70);

  background: rgba(0, 0, 0, 0.66);
  backdrop-filter: blur(3px);
}

.roulette-error {
  @apply relative flex flex-col items-center text-center gap-3;

  width: 70%;
  padding: 20px;
  border-radius: 18px;
  background: #0b1428;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);

  img {
    -webkit-user-drag: none;
  }

  &__close {
    @apply absolute flex items-center justify-center;

    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;

    img {
      @apply w-full h-full;
    }
  }

  // Selo de alerta (CSS puro, sem asset): círculo vermelho com "!".
  &__icon {
    @apply flex items-center justify-center font-extrabold text-white;

    width: 56px;
    height: 56px;
    font-size: 30px;
    line-height: 1;
    border-radius: 999px;
    background: radial-gradient(circle at 50% 35%, #ff5a5a 0%, #d32f2f 70%);
    box-shadow:
      0 6px 16px rgba(211, 47, 47, 0.45),
      inset 0 2px 6px rgba(255, 255, 255, 0.35);
  }

  &__title {
    @apply text-texts text-lg font-bold;
  }

  &__message {
    @apply text-texts/80 text-sm;
  }

  &__actions {
    @apply flex flex-col gap-2 w-full mt-2;
  }

  &__btn {
    @apply w-full rounded-xl py-3 px-4 font-bold text-white text-center;

    background: #4f46e5;
    border: 1px solid transparent;
  }

  &__btn--ghost {
    @apply text-white/90;

    background: transparent;
    border-color: rgba(255, 255, 255, 0.25);
  }
}

// Pop-in do card (via @keyframes por causa do reset global de transition).
.rlt-popup-enter-active {
  animation: rlt-err-in 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);

  .roulette-error {
    animation: rlt-err-card-in 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);
  }
}

.rlt-popup-leave-active {
  animation: rlt-err-in 0.16s ease-in reverse;
}

@keyframes rlt-err-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes rlt-err-card-in {
  from {
    transform: scale(0.92);
  }
  to {
    transform: scale(1);
  }
}
</style>
